/**
 * @fileoverview Search-surface telemetry capture-map (FRE-822, extended in FRE-824).
 *
 * Single source of truth for the six PostHog events emitted by the global
 * `SearchSheet` + `SearchAutocomplete` surface:
 *
 * | PostHog event name        | Trigger                                      | Payload                                                            |
 * | ------------------------- | -------------------------------------------- | ------------------------------------------------------------------ |
 * | `search_opened`           | Sheet opens                                  | `{ trigger: 'button' \| 'kbd' \| 'slash' }`                        |
 * | `search_query_typed`      | Debounce settles (max 1 per finalized query) | `{ queryLength: number, resultCount: number }`                     |
 * | `search_result_clicked`   | User clicks / Enters a suggestion            | `{ category: 'artist'\|'venue'\|'event'\|'location', position }`   |
 * | `search_recent_used`      | User clicks a recent search                  | `{ position: number }`                                             |
 * | `search_popular_clicked`  | User clicks a curated popular search         | `{ position: number }`                                             |
 * | `search_dismissed`        | Sheet closes                                 | `{ reason: 'escape'\|'backdrop'\|'select'\|'navigation' }`         |
 *
 * **Privacy invariant — never leak raw query text.** Funnel analytics only
 * need to know that a search happened and how long the query was — never
 * the literal characters the user typed. The {@link SearchQueryTypedEvent}
 * payload is intentionally restricted to `queryLength: number` so a future
 * refactor cannot accidentally smuggle the query string through. The unit
 * tests assert this invariant by serializing every payload and grepping
 * for forbidden substrings.
 *
 * **Consent gate.** All capture flows through `lib/analytics.ts#track`,
 * which is itself gated by the WS-6 cookie-consent decision (FRE-809):
 *   - No consent decision → no capture, no `/decide` request.
 *   - `denied` → no capture.
 *   - `granted` → capture proceeds.
 *
 * This module deliberately does NOT import `posthog-js` directly. The
 * canonical client wrapper in `lib/analytics.ts` registers the PostHog
 * client late (after consent) and exposes a `track()` no-op when consent
 * has not been granted yet. Routing every capture through that wrapper
 * keeps the consent gate honest in one place.
 */
import { track } from '@/lib/analytics';

/* ------------------------------------------------------------------ */
/*  PostHog event-name map                                             */
/* ------------------------------------------------------------------ */

/**
 * Canonical PostHog event names for the search surface. Exported so
 * downstream dashboards and tests can reference the same string constants
 * the runtime emits — no string drift between code and analytics.
 */
export const SEARCH_EVENT_NAMES = {
  SEARCH_OPENED: 'search_opened',
  SEARCH_QUERY_TYPED: 'search_query_typed',
  SEARCH_RESULT_CLICKED: 'search_result_clicked',
  SEARCH_RECENT_USED: 'search_recent_used',
  SEARCH_POPULAR_CLICKED: 'search_popular_clicked',
  SEARCH_DISMISSED: 'search_dismissed',
} as const;

/** Union of the five PostHog event names emitted by this module. */
export type SearchEventName = (typeof SEARCH_EVENT_NAMES)[keyof typeof SEARCH_EVENT_NAMES];

/* ------------------------------------------------------------------ */
/*  Payload schemas — one type per event                               */
/* ------------------------------------------------------------------ */

/** How the user triggered the SearchSheet open. */
export type SearchOpenTrigger = 'button' | 'kbd' | 'slash';

/** Category of the clicked suggestion — mirrors `SearchSuggestionItem.type` minus `recent` / `popular`. */
export type SearchResultCategory = 'artist' | 'venue' | 'event' | 'location';

/** Why the SearchSheet closed. */
export type SearchDismissReason = 'escape' | 'backdrop' | 'select' | 'navigation';

interface SearchOpenedEvent {
  type: 'search_opened';
  trigger: SearchOpenTrigger;
}

interface SearchQueryTypedEvent {
  type: 'search_query_typed';
  /**
   * Length of the user-typed query at the moment the debounce settled.
   * **MUST NOT** be the query text — only its character count. This is the
   * load-bearing privacy invariant for the search surface; do not add a
   * `query` / `text` / `value` field to this shape.
   */
  queryLength: number;
  /** Number of suggestion results rendered for this finalized query. */
  resultCount: number;
}

interface SearchResultClickedEvent {
  type: 'search_result_clicked';
  category: SearchResultCategory;
  /** 0-indexed position of the clicked row within the full rendered list. */
  position: number;
}

interface SearchRecentUsedEvent {
  type: 'search_recent_used';
  /** 0-indexed position of the recent-search row within the recent list. */
  position: number;
}

interface SearchPopularClickedEvent {
  type: 'search_popular_clicked';
  /**
   * 0-indexed position of the clicked row within the full rendered list
   * order (recents → popular → suggestions). Matches the position math
   * used by {@link SearchResultClickedEvent} and {@link SearchRecentUsedEvent}
   * — `handleSelect` in `SearchAutocomplete` derives this from the same
   * `flatItems` array that powers keyboard navigation, so a popular row
   * that visually appears below 2 recents reports `position: 2`.
   */
  position: number;
}

interface SearchDismissedEvent {
  type: 'search_dismissed';
  reason: SearchDismissReason;
}

/**
 * Discriminated union of every search-surface event. Adding a new event
 * means: (1) adding a name to {@link SEARCH_EVENT_NAMES}, (2) adding a
 * payload interface above, (3) appending it to this union, and (4)
 * extending the switch in {@link emitSearchEvent}. The TypeScript compiler
 * will refuse to build if any of the four are skipped.
 */
export type SearchEvent =
  | SearchOpenedEvent
  | SearchQueryTypedEvent
  | SearchResultClickedEvent
  | SearchRecentUsedEvent
  | SearchPopularClickedEvent
  | SearchDismissedEvent;

/* ------------------------------------------------------------------ */
/*  Emitter                                                             */
/* ------------------------------------------------------------------ */

/**
 * Fire a single search-surface telemetry event through the consent-gated
 * `lib/analytics.ts#track` wrapper.
 *
 * No-op when the user has not granted analytics consent (FRE-809). Never
 * throws — analytics must not break the search UX.
 *
 * @example
 * ```ts
 * emitSearchEvent({ type: 'search_opened', trigger: 'button' });
 * emitSearchEvent({ type: 'search_query_typed', queryLength: 8, resultCount: 3 });
 * emitSearchEvent({ type: 'search_result_clicked', category: 'artist', position: 0 });
 * ```
 */
export function emitSearchEvent(event: SearchEvent): void {
  switch (event.type) {
    case 'search_opened': {
      track(SEARCH_EVENT_NAMES.SEARCH_OPENED, { trigger: event.trigger });
      return;
    }
    case 'search_query_typed': {
      // Payload restricted to numerics by construction — see the privacy
      // note on `SearchQueryTypedEvent` above.
      track(SEARCH_EVENT_NAMES.SEARCH_QUERY_TYPED, {
        queryLength: event.queryLength,
        resultCount: event.resultCount,
      });
      return;
    }
    case 'search_result_clicked': {
      track(SEARCH_EVENT_NAMES.SEARCH_RESULT_CLICKED, {
        category: event.category,
        position: event.position,
      });
      return;
    }
    case 'search_recent_used': {
      track(SEARCH_EVENT_NAMES.SEARCH_RECENT_USED, { position: event.position });
      return;
    }
    case 'search_popular_clicked': {
      track(SEARCH_EVENT_NAMES.SEARCH_POPULAR_CLICKED, { position: event.position });
      return;
    }
    case 'search_dismissed': {
      track(SEARCH_EVENT_NAMES.SEARCH_DISMISSED, { reason: event.reason });
      return;
    }
    default: {
      // Exhaustiveness check — if a new event variant is added to
      // `SearchEvent` without a matching case here, this assignment fails
      // to compile.
      const _exhaustive: never = event;
      void _exhaustive;
    }
  }
}
